import React, { useEffect, useState, useCallback, useRef } from "react";
import { gsap } from "gsap";
import { tenantFetch, getTenantId, isPrimaryOccupant, API_BASE, parseArrayData, getAssignedBuildings } from "./TenantAuth";
import { useTenantHeader } from "./TenantLayout";
import toast from "react-hot-toast";
import { applyBillingEnumText } from "../../utils/enums";

const PAGE_SIZE = 20;

function fmtMoney(v) {
  if (v == null || v === "") return "—";
  return new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(Number(v));
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function getInitials(first = "", last = "") {
  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  const clean = (first || last || "").replace(/^(mr|mrs|ms|miss|dr|prof|engr|arch|barr|alh|alhaji)\.?\s+/i, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (clean.slice(0, 2) || "??").toUpperCase();
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

function ModalShell({ modalRef, icon, title, onClose, closeDisabled, subheader, footer, overlay, width = 720, fill = false, children }) {
  return (
    <div className="modal fade" ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }}>
        <div className="modal-content d6m">
          {overlay}
          <div className="d6m-head">
            <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
            <div className="min-w-0 flex-grow-1">
              <h3 className="d6m-title text-truncate">{title}</h3>
            </div>
            <button type="button" className="d6m-x" onClick={onClose} disabled={closeDisabled} aria-label="Close">
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          {subheader}
          <div className={`d6m-body ${fill ? "d6m-body-fill" : ""}`}>{children}</div>
          {footer && <div className="d6m-foot">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

function Avatar({ src, name, size = 48, onClick }) {
  const initials = getInitials(name || "");
  return (
    <span
      className={`d6m-avatar ${onClick ? "cursor-pointer" : ""}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      onClick={onClick}
      title={onClick ? "View photo" : undefined}
    >
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
}

function CardHead({ title, subtitle, children, className = "mb-4" }) {
  return (
    <div className={`d-flex align-items-start justify-content-between flex-wrap gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-gray-900 d6-title mb-1">{title}</h3>
        {subtitle && <span className="text-gray-400 fs-8">{subtitle}</span>}
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay d6-topnav-preloader ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedroom Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedrooms Duplex": "/assets/media/zangon/4bd.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "5 Bedrooms Terrace": "/assets/media/zangon/5-light.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");
  
  if (norm.includes("1bedroom") || norm.includes("1bd")) return "/assets/media/zangon/1bedroombungalo.jpg";
  if (norm.includes("2bedroom") || norm.includes("2bd")) return "/assets/media/zangon/2bedroom.jpg";
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo")) return "/assets/media/zangon/4bedroombungalo.jpg";
  if (norm.includes("4bedroomduplex") || norm.includes("4bd")) return "/assets/media/zangon/4bd.jpg";
  if (norm.includes("5bedroom")) return "/assets/media/zangon/3bedroomsbungalo.jpg"; 
  if (norm.includes("6bedroombungalow")) return "/assets/media/zangon/6bedroombungalow.jpg";
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) {
    return "/assets/media/zangon/6bedroomterraceduple.jpg";
  }
  
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}

// ─── Building Modal with Occupants, QR Codes, and Details ───────────────────
function BuildingModal({ modalRef, building, onClose }) {
  const { hide } = useBootstrapModal(modalRef);
  const tenantId   = getTenantId();
  const isOccupant = isPrimaryOccupant();
  const rootPath   = isOccupant ? `primary-occupants/${tenantId}` : `tenants/${tenantId}`;

  const [activeRent, setActiveRent] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleClose = () => {
    hide();
    onClose?.();
  };

  const fetchData = useCallback(async () => {
    if (!building?.buildingId) return;
    setLoading(true);
    try {
      const res = await tenantFetch(`${API_BASE}/${rootPath}/buildings/${building.buildingId}/active-rent`);
      const json = res.ok ? applyBillingEnumText(await res.json()) : null;
      setActiveRent(json?.data || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [building?.buildingId, rootPath]);

  useEffect(() => {
    if (building?.buildingId) {
      fetchData();
    }
  }, [building?.buildingId, fetchData]);

  if (!building) {
    return (
      <ModalShell
        modalRef={modalRef}
        icon="ki-home-2"
        title="Building Details"
        onClose={handleClose}
        width={720}
      >
        <div className="d6m-empty">No building selected.</div>
      </ModalShell>
    );
  }

  const unitLabel = String(building.number).startsWith("Staff Quarter") ? building.number : `House ${building.number}`;

  const baseRent = activeRent?.rentAmount != null 
    ? activeRent.rentAmount 
    : (building.currentRent?.rentAmount != null ? building.currentRent.rentAmount : (building.estimatedInvoiceTotal ?? 0));

  const scComp = activeRent?.components?.find(c => /service charge/i.test(c.name || c.displayName || c.type || c.code || ""))
    || building.currentRent?.components?.find(c => /service charge/i.test(c.name || c.displayName || c.type || c.code || ""));
  const serviceCharge = scComp?.amount != null ? scComp.amount : (building.serviceCharge || 1000000);

  const extraComponents = (activeRent?.components || []).filter(c => {
    const n = (c.displayName || c.name || "").trim().toLowerCase();
    return n !== "rent" && !n.includes("service charge");
  });

  const nextDueDate = activeRent?.nextDueDate || building.currentRent?.nextDueDate;
  const extraChargesTotal = extraComponents.reduce((acc, c) => acc + (Number(c.amount) || 0), 0);
  const totalEst = activeRent?.estimatedInvoiceTotal && activeRent.estimatedInvoiceTotal > (baseRent + serviceCharge)
    ? activeRent.estimatedInvoiceTotal
    : (Number(baseRent) + Number(serviceCharge) + extraChargesTotal);

  const hasFinancialInfo = activeRent || baseRent > 0;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-home-2"
      title={`${unitLabel} — Details`}
      onClose={handleClose}
      width={720}
      subheader={
        <div className="px-6 py-2 bg-light border-bottom border-gray-100 d-flex align-items-center justify-content-between">
          <span className="fs-8 text-gray-600">
            {building.phaseName} • {building.typeName}
          </span>
          <StatusChip color="#0EA84A">Linked Property</StatusChip>
        </div>
      }
      footer={
        <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>
          Close
        </button>
      }
    >
      {loading ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-12">
          <span className="spinner-border spinner-border-lg text-success mb-3" />
          <span className="d6m-muted">Loading property details…</span>
        </div>
      ) : (
        <div className="row g-6">
          {/* Right Column: Building Details & Financials */}
          <div className="col-12">
            {/* Building Image */}
            <div
              className="position-relative rounded-3 overflow-hidden d-flex align-items-center justify-content-center mb-4 border border-gray-200"
              style={{ width: "100%", height: "160px" }}
            >
              <img 
                alt={building.typeName} 
                src={getHouseImage(building.typeName)} 
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} 
              />
              <div
                className="position-absolute"
                style={{
                  inset: 0,
                  background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)",
                }}
              />
              <div className="position-absolute bottom-0 start-0 p-3">
                <span className="badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1">
                  {unitLabel}
                </span>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="d6m-section mt-0">
              <div className="d6m-section-head">
                <span>Financial &amp; Rent Summary</span>
              </div>
              {hasFinancialInfo ? (
                <div className="d6m-invoice">
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <div className="d6m-amount text-success">{fmtMoney(totalEst)}</div>
                      <div className="d6m-muted mt-1">Total Estimated Rent &amp; Charges</div>
                    </div>
                    {nextDueDate && (
                      <StatusChip color="#16A34A">
                        Due {fmtDate(nextDueDate)}
                      </StatusChip>
                    )}
                  </div>

                  <div className="d6m-lines">
                    <div className="d6m-line">
                      <span>Base Rent Amount</span>
                      <span className="fw-semibold text-gray-900">{fmtMoney(baseRent)}</span>
                    </div>
                    <div className="d6m-line">
                      <span>Estate Service Charge</span>
                      <span className="fw-semibold text-gray-900">{fmtMoney(serviceCharge)}</span>
                    </div>
                    {extraComponents.map((c, i) => (
                      <div key={i} className="d6m-line">
                        <span>{c.displayName || c.name || "Charge"}</span>
                        <span className="fw-semibold text-gray-900">{fmtMoney(c.amount)}</span>
                      </div>
                    ))}
                    <div className="d6m-line d6m-line-total">
                      <span>Total Estimated</span>
                      <span className="text-success">{fmtMoney(totalEst)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="d6m-empty">
                  No active rent configured for this building.
                </div>
              )}
            </div>

            {/* Property Overview Grid */}
            <div className="d6m-section">
              <div className="d6m-section-head">
                <span>Property Details</span>
              </div>
              <div className="d6m-kv d6m-kv-4">
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Phase</span>
                  <span className="d6m-v text-truncate d-block">{building.phaseName || "—"}</span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Property Type</span>
                  <span className="d6m-v text-truncate d-block" title={building.typeName}>{building.typeName || "—"}</span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Date Assigned</span>
                  <span className="d6m-v">{fmtDate(building.assignedAt)}</span>
                </div>
                <div className="d6m-kv-cell">
                  <span className="d6m-k">Primary Occupant</span>
                  <span className="d6m-v text-truncate d-block" title={building.primaryOccupant || "—"}>
                    {building.primaryOccupant || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ─── Main page ────────────────────────────────────────────────
export default function TenantBuildings() {
  const dashboardRef = useRef(null);
  const tenantId   = getTenantId();
  const [loading,  setLoading]  = useState(true);
  const [buildings, setBuildings] = useState([]);
  const [search,    setSearch]    = useState("");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [rentFilter, setRentFilter] = useState("all");
  const [viewMode,  setViewMode]  = useState("grid"); // "grid" | "list"
  const [selected,  setSelected]  = useState(null);
  const [page,      setPage]      = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const buildingModalRef = useRef(null);
  const buildingModal = useBootstrapModal(buildingModalRef);

  const fetchBuildings = useCallback(async (pg = 0) => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const json = await getAssignedBuildings({ pageIndex: pg + 1, pageSize: PAGE_SIZE, search, id: tenantId });
      const data = parseArrayData({ data: json });
      setBuildings(data);
      setTotalCount(json?.totalCount || json?.data?.totalCount || data.length);
      setPage(pg);
    } catch {
      toast.error("Failed to load buildings.");
    } finally {
      setLoading(false);
    }
  }, [tenantId, search]);

  useEffect(() => {
    fetchBuildings(0);
  }, [fetchBuildings]);

  useEffect(() => {
    if (loading) return undefined;
    const context = gsap.context(() => {
      gsap.fromTo(".gsap-hero", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.06, duration: 0.55, ease: "power3.out", delay: 0.05, clearProps: "all" });
      gsap.fromTo(".gsap-card", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.08, duration: 0.55, ease: "power2.out", delay: 0.2, clearProps: "all" });
    }, dashboardRef);
    return () => context.revert();
  }, [loading]);

  const { setHeaderConfig } = useTenantHeader();

  useEffect(() => {
    setHeaderConfig({
      title: "Building Management",
      breadcrumb: "Building Management",
      actions: null,
    });
    return () => setHeaderConfig({ title: "", breadcrumb: "", actions: null });
  }, [setHeaderConfig]);

  const openBuilding = (b) => {
    setSelected(b);
    buildingModal.show();
  };

  const phases  = ["all", ...new Set(buildings.map(b => b.phaseName).filter(Boolean))];
  const filtered = buildings.filter(b => {
    const matchPhase = phaseFilter === "all" || b.phaseName === phaseFilter;
    const isDue = b.currentRent?.isDue || (b.currentRent?.nextDueDate && new Date(b.currentRent.nextDueDate) < new Date());
    const matchRent = rentFilter === "all" || (rentFilter === "due" ? isDue : !isDue);
    return matchPhase && matchRent;
  });

  return (
    <>
      <PagePreloader visible={loading} />

      <div ref={dashboardRef} className="app-container container-fluid py-6 px-6 px-lg-10">

        {/* Green Top Stats Section */}
        <div className="d6-kpi-brand p-6 mb-5">
          <div className="row g-4 g-xl-0 align-items-center">
            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-home-2 fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Assigned Buildings</div>
                    <div className="d6-num text-white my-0.5">{totalCount}</div>
                    <div className="text-white text-opacity-70 fs-9">Linked properties</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-shield-tick fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Active Leases</div>
                    <div className="d6-num text-white my-0.5">{buildings.length}</div>
                    <div className="text-white text-opacity-70 fs-9">Current occupancy</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-bill fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Due Rent Invoices</div>
                    <div className="d6-num text-white my-0.5">
                      {buildings.filter(b => b.currentRent?.isDue || (b.currentRent?.nextDueDate && new Date(b.currentRent.nextDueDate) < new Date())).length}
                    </div>
                    <div className="text-white text-opacity-70 fs-9">Pending settlement</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-people fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Residents &amp; Staff</div>
                    <div className="d6-num text-white my-0.5">—</div>
                    <div className="text-white text-opacity-70 fs-9">Registered occupants</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      
        {/* Main Content Card */}
        <div className="d6-card p-6 mb-5 gsap-card">
          <CardHead
            title="My Buildings"
            subtitle={`Search & filter propert${totalCount !== 1 ? "ies" : "y"}`}
            className="mb-5"
          >
            
            <div className="position-relative">
              <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
              <input
                type="text"
                className="d6m-input ps-10"
                style={{ width: 220, minHeight: 40, height: 40, borderRadius: 999 }}
                placeholder="Search by house or name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <select
              className="d6m-input"
              style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }}
              value={phaseFilter}
              onChange={e => setPhaseFilter(e.target.value)}
            >
              {phases.map(p => (
                <option key={p} value={p}>{p === "all" ? "All Phases" : p}</option>
              ))}
            </select>

            <select
              className="d6m-input"
              style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }}
              value={rentFilter}
              onChange={e => setRentFilter(e.target.value)}
            >
              <option value="all">All Rent Status</option>
              <option value="due">Due Rent</option>
              <option value="nodue">No Due Rent</option>
            </select>

            <div className="d6-seg">
              <button
                type="button"
                className={`fs-8 fw-bold ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid view"
              >
                <i className="ki-outline ki-element-11 fs-5 m-0"></i>
              </button>
              <button
                type="button"
                className={`fs-8 fw-bold ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                title="List view"
              >
                <i className="ki-outline ki-row-horizontal fs-5 m-0"></i>
              </button>
            </div>
          </CardHead>

          <div>
            {viewMode === "grid" && (
              <div className="row g-5">
                {filtered.length === 0 && !loading && (
                  <div className="col-12">
                    <div className="d6m-empty py-12">
                      <i className="ki-outline ki-home-2 fs-3x text-gray-300 d-block mb-3"></i>
                      No buildings found matching your criteria.
                    </div>
                  </div>
                )}
                {filtered.map(b => {
                  const rent = b.currentRent?.rentAmount != null ? b.currentRent.rentAmount : (b.estimatedInvoiceTotal ?? 0);
                  const serviceCharge = b.currentRent?.components?.find(c => /service charge/i.test(c.name || c.displayName))?.amount || 1000000;
                  const houseImg = getHouseImage(b.typeName);
                  const isDue = b.currentRent?.isDue || (b.currentRent?.nextDueDate && new Date(b.currentRent.nextDueDate) < new Date());

                  return (
                    <div key={b.buildingId} className="col-12 col-md-6 col-xl-4">
                      <div
                        className={`d6-card h-100 d-flex flex-column cursor-pointer ${isDue ? "border-hover-danger" : "border-hover-success"} transition-all overflow-hidden`}
                        onClick={() => openBuilding(b)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openBuilding(b); }}
                      >
                        <div className="position-relative overflow-hidden" style={{ height: 180 }}>
                          <img
                            alt={b.typeName || "House"}
                            src={houseImg}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                          <div
                            className="position-absolute"
                            style={{
                              inset: 0,
                              background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.65) 100%)",
                            }}
                          />
                          <div className="position-absolute bottom-0 start-0 p-3 d-flex align-items-center gap-2">
                            <span className="badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1">
                              {b.number}
                            </span>
                            <span className="badge bg-success text-white fs-8 fw-semibold rounded-pill px-3 py-1">
                              {b.phaseName || "Estate"}
                            </span>
                          </div>
                        </div>

                        <div className="p-4 d-flex flex-column flex-grow-1">
                          <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                            <div className="min-w-0">
                              <h4 className="d6-title text-gray-900 mb-1 text-truncate">
                                {b.typeName || "Standard Unit"}
                              </h4>
                              <span className="d6m-muted fs-8">
                                {b.assignedAt ? `Assigned ${fmtDate(b.assignedAt)}` : "Assigned Property"}
                              </span>
                            </div>
                            <StatusChip color={isDue ? "#DC2626" : "#16A34A"}>
                              {isDue ? "Due" : "Active"}
                            </StatusChip>
                          </div>

                          <div className="d-flex align-items-center justify-content-between mt-auto pt-3 border-top border-gray-100">
                            <div className="d-flex flex-column min-w-0">
                              <span className="d6m-k mb-0 fs-9">Base Rent</span>
                              <div className="d-flex align-items-baseline gap-2">
                                <span className="d6-title text-success fs-7 mb-0">{fmtMoney(rent)}</span>
                                {serviceCharge > 0 && (
                                  <span className="text-gray-400 fs-9 text-truncate">+{fmtMoney(serviceCharge)} Service Charge</span>
                                )}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="d6-icon-btn" title="View details">
                                <i className="ki-outline ki-arrow-up-right fs-5"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {viewMode === "list" && (
              <div className="d-flex flex-column gap-3">
                {filtered.length === 0 && !loading && (
                  <div className="d6m-empty py-12">
                    <i className="ki-outline ki-home-2 fs-3x text-gray-300 d-block mb-3"></i>
                    No buildings found matching your criteria.
                  </div>
                )}
                {filtered.map(b => {
                  const rent = b.currentRent?.rentAmount != null ? b.currentRent.rentAmount : (b.estimatedInvoiceTotal ?? 0);
                  const serviceCharge = b.currentRent?.components?.find(c => /service charge/i.test(c.name || c.displayName))?.amount || 1000000;
                  const houseImg = getHouseImage(b.typeName);
                  const isDue = b.currentRent?.isDue || (b.currentRent?.nextDueDate && new Date(b.currentRent.nextDueDate) < new Date());
                  const isSpecialName = String(b.number).startsWith("Staff Quarter") || String(b.number).startsWith("House");
                  const titleText = b.phaseName ? `${b.phaseName} - ${isSpecialName ? b.number : `House ${b.number}`}` : (isSpecialName ? b.number : `House ${b.number}`);
                  const occupantsCount = b.occupantsCount != null ? b.occupantsCount : (b.occupantCount != null ? b.occupantCount : (b.primaryOccupants?.length || b.dependantsCount || 1));

                  return (
                    <div
                      key={b.buildingId}
                      className={`d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 ${isDue ? "border-hover-danger" : "border-hover-success"} position-relative overflow-hidden cursor-pointer transition-all`}
                      style={{ minHeight: 90 }}
                      onClick={() => openBuilding(b)}
                    >
                      <div className={`position-absolute top-0 start-0 h-100 ${isDue ? "bg-danger" : "bg-success"} w-4px`}></div>
                      
                      <div className="d-flex align-items-center me-4" style={{ width: "380px", flexShrink: 0 }}>
                        <Avatar src={houseImg} name={b.number || b.typeName} size={48} />
                        <div className="d-flex flex-column overflow-hidden ms-3 min-w-0">
                          <span className="fs-6 fw-bold text-gray-900 text-truncate mb-0.5">
                            {titleText}
                          </span>
                          <span className="text-gray-500 fs-8 text-truncate">
                            {b.typeName || "Standard Unit"} · {b.phaseName}
                          </span>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0 flex-wrap">
                        <div className="d-none d-sm-block text-center">
                          <div className="text-gray-400 fs-9 mb-0.5">Base Rent</div>
                          <div className="fw-bold text-success fs-8">{fmtMoney(rent)}</div>
                        </div>
                        <div className="d-none d-md-block text-center">
                          <div className="text-gray-400 fs-9 mb-0.5">Service Charge</div>
                          <div className="fw-semibold text-gray-700 fs-8">{fmtMoney(serviceCharge)}</div>
                        </div>
                        <div className="d-none d-lg-block text-center">
                          <div className="text-gray-400 fs-9 mb-0.5">Total Est.</div>
                          <div className="fw-bold text-gray-900 fs-8">{fmtMoney(b.currentRent?.estimatedInvoiceTotal ?? (rent + serviceCharge))}</div>
                        </div>
                        <div className="d-none d-sm-block text-center">
                          <div className="text-gray-400 fs-9 mb-0.5">Next Due</div>
                          <div className={`fw-semibold fs-8 ${isDue ? "text-danger" : "text-gray-900"}`}>{fmtDate(b.currentRent?.nextDueDate)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-400 fs-9 mb-0.5">Occupants</div>
                          <div className="fw-bold text-gray-900 fs-8">{occupantsCount}</div>
                        </div>
                        <div className="d-flex align-items-center ms-2">
                          <span className="d6-icon-btn" title="View details">
                            <i className="ki-outline ki-arrow-up-right fs-5"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            <div className="d-flex align-items-center justify-content-between mt-6 pt-4 border-top border-gray-100 flex-wrap gap-3">
              <div className="text-gray-500 fs-8">
                Showing {totalCount > 0 ? page * PAGE_SIZE + 1 : 0}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount} buildings
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light d6-mbtn px-3"
                  style={{ height: 32 }}
                  disabled={page === 0}
                  onClick={() => fetchBuildings(page - 1)}
                >
                  <i className="ki-outline ki-left fs-6 m-0"></i>
                </button>
                <span className="btn btn-sm btn-success d6-mbtn px-3 py-0" style={{ height: 32 }}>
                  {page + 1}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-light d6-mbtn px-3"
                  style={{ height: 32 }}
                  disabled={(page + 1) * PAGE_SIZE >= totalCount}
                  onClick={() => fetchBuildings(page + 1)}
                >
                  <i className="ki-outline ki-right fs-6 m-0"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BuildingModal
        modalRef={buildingModalRef}
        building={selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
}
